import React, { useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import NewsletterFormComponent from '@fuww/library/src/NewsletterForm';
import { ApolloError, useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { enqueueSnackbar } from 'notistack';
import messages from '../../lib/messages.json';
import getEmailSuggestionFormatter from '../../lib/getEmailSuggestionFormatter';
import showNewsletterSignup from '../../lib/showNewsletterSignup';
import subscribeToNewsletterMutation
  from '../../lib/mutations/subscribeToNewsletter.graphql';
import { useSiteContext } from '../SiteContext';
import { useCaptcha } from '../Captcha';

const newsletterFormPropertyTypes = {
  showDescriptiveText: PropTypes.bool,
};

type NewsletterFormProperties = InferProps<
  typeof newsletterFormPropertyTypes
>;

const NewsletterForm = (
  { showDescriptiveText } : NewsletterFormProperties,
) => {
  const intl = useIntl();
  const { route } = useRouter();
  const { locale } = useSiteContext();
  const { executeRecaptcha } = useCaptcha();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [graphqlError, setGraphqlError] = useState<ApolloError | null>(null);

  const [subscribeToNewsletter] = useMutation(subscribeToNewsletterMutation, {
    onError: (error) => {
      setGraphqlError(error);
      setSubmitDisabled(false);
    },
  });

  const getSubmitHandler = (
    callback: () => void,
  ) => async ({ email }: { email: string }) => {
    setSubmitDisabled(true);
    setGraphqlError(null);
    subscribeToNewsletter({
      variables: {
        email,
        locale,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        captchaResponse: await executeRecaptcha!('subscribe'),
      },
      onCompleted: () => {
        enqueueSnackbar(
          intl.formatMessage(messages['newsletterForm.successMessage']),
          { variant: 'info' },
        );
        setSubmitDisabled(false);
        callback();
      },
    });
  };
  return route === '/newsletter'
  || (route !== '/news-form' && showNewsletterSignup()) ? (
    <NewsletterFormComponent
      title={intl.formatMessage(messages['newsletterForm.title'])}
      description={(
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(
              messages['newsletterForm.description'],
            ),
          }}
        />
      )}
      emailValidationMessage={intl.formatMessage(messages[
        'applicantForm.mustBeValidEmailAddress'
      ])}
      requiredValidationMessage={intl.formatMessage(messages[
        'applicantForm.fieldRequired'
      ])}
      emailAddressLabel={intl.formatMessage(messages.emailAddress)}
      formatEmailSuggestion={getEmailSuggestionFormatter(intl)}
      disclaimerMessage={(
        <small
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(
              messages['newsletterForm.disclaimerMessage'],
            ),
          }}
        />
      )}
      submitLabel={intl.formatMessage(messages[
        'newsletterForm.submitLabel'
      ])}
      showDescriptiveText={showDescriptiveText}
      getSubmitHandler={getSubmitHandler}
      submitDisabled={submitDisabled}
      graphqlError={graphqlError}
    />
    ) : null;
};

NewsletterForm.propTypes = newsletterFormPropertyTypes;

export default NewsletterForm;
